import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapIcon,
  PencilIcon,
  LockClosedIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";

export default function LandingPage() {
  const navigate = useNavigate();

  function handleLogin() {
    navigate("/login");
  }

  function handleSignup() {
    navigate("/signup");
  }

  return (
    <div className="bg-surface-elevated text-text-primary font-sans">
      {/* Hero */}
      <section className="min-h-screen flex flex-col md:flex-row items-center justify-between px-8 md:px-20 py-20 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-xl">
          <h1 className="text-5xl font-bold mb-6 leading-tight tracking-tight">
            Traces
            <br />
            <span className="text-primary">Leave your mark.</span>
          </h1>
          <p className="text-text-secondary text-lg mb-8">
            Traces lets you pin thoughts, memories, or media to physical places
            so others can discover them in context.
          </p>
          <div className="flex gap-4">
            <button
              className="px-6 py-3 bg-black text-text-inverse rounded-md text-sm hover:bg-surface-dark transition"
              onClick={handleSignup}
            >
              Get Started
            </button>
            <button
              className="px-6 py-3 border border-strong text-text-secondary rounded-md text-sm hover:bg-surface-sunken transition"
              onClick={handleLogin}
            >
              Log In
            </button>
          </div>
        </div>
        <div className="mt-12 md:mt-0">
          <img
            src="https://dubxj45tqj459.cloudfront.net/ApplicationImages/map_screenshot.png"
            alt="Map preview"
            className="w-full max-w-[480px] rounded-xl shadow-md border"
          />
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 md:px-20 bg-surface-sunken">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold">What Traces Can Do</h2>
          <p className="text-text-muted mt-3 max-w-xl mx-auto">
            Built for people who want to connect places to purpose.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 max-w-5xl mx-auto text-center">
          <FeatureItem
            icon={<MapIcon className="h-6 w-6 text-primary" />}
            title="Map-Based Notes"
            description="Leave content pinned to specific coordinates."
          />
          <FeatureItem
            icon={<PencilIcon className="h-6 w-6 text-primary" />}
            title="Rich Notes"
            description="Attach images, write your thoughts, and revisit memories."
          />
          <FeatureItem
            icon={<LockClosedIcon className="h-6 w-6 text-primary" />}
            title="Private or Public"
            description="Choose your visibility: personal or discoverable."
          />
          <FeatureItem
            icon={<GlobeAltIcon className="h-6 w-6 text-primary" />}
            title="Discover Nearby"
            description="See what others are leaving behind at nearby locations."
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-surface-sunken py-20 px-8 md:px-20 text-center">
        <h2 className="text-3xl font-bold mb-4">Start Leaving Traces</h2>
        <p className="text-text-secondary mb-8">
          Pin your first memory to the map and join a community of explorers.
        </p>
        <button
          className="px-8 py-3 bg-primary text-text-inverse rounded-md text-sm hover:bg-gemini-accent transition"
          onClick={handleSignup}
        >
          Sign Up Free
        </button>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 md:px-20 border-t text-sm text-text-muted flex flex-col md:flex-row justify-between">
        <span>© {new Date().getFullYear()} Traces. All rights reserved.</span>
        <div className="flex gap-6 mt-4 md:mt-0">
          <Link to="/about" className="hover:text-text-primary transition">
            About
          </Link>
          <Link to="/contact" className="hover:text-text-primary transition">
            Contact
          </Link>
          <Link to="/privacy" className="hover:text-text-primary transition">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-text-primary transition">
            Terms
          </Link>
          <Link
            to="/community-guidelines"
            className="hover:text-text-primary transition"
          >
            Guidelines
          </Link>
        </div>
      </footer>
    </div>
  );
}

function FeatureItem({ icon, title, description }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 rounded-full bg-primary-800 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="text-sm text-text-muted mt-2 max-w-[200px]">
        {description}
      </p>
    </div>
  );
}
